import { useCallback, useEffect, useMemo, useState } from 'react'
import Modal from '@common/Modal'
import { userRating } from '@/api'
import { IcoRemove } from '@pages/Admin/constsIcon'
import { useSelector } from 'react-redux'
import { isInterfaceAnimationsDisabled } from '@/helpers/interfaceMotionSettings'
import s from './RatingChangeNotifications.module.css'

const prepareNotificationItems = (items) => {
  if (!Array.isArray(items)) return []

  const validItems = items.filter(Boolean)

  if (validItems.length < 2) return validItems

  const hasUpItems = validItems.some((item) => item?.direction === 'up')
  const hasDownItems = validItems.some((item) => item?.direction === 'down')

  return [
    {
      direction: hasUpItems && hasDownItems ? 'mixed' : validItems[0]?.direction,
      isSummary: true,
      items: validItems,
    },
  ]
}

const getPositionText = (item) => {
  if (!item) return ''

  if (item.previous_position) {
    return `с ${item.previous_position} до ${item.position}`
  }

  return `до ${item.position}`
}

const getSummaryText = (direction) => {
  if (direction === 'up') {
    return 'Место в рейтинге изменилось сразу в нескольких разделах. Так держать!'
  }

  if (direction === 'down') {
    return 'Место в рейтинге изменилось сразу в нескольких разделах. Поднажми и у тебя всё получится!'
  }

  return 'Место в рейтинге изменилось сразу в нескольких разделах.'
}

const getNotificationKeys = (notifications) => {
  return notifications.reduce((result, item) => {
    const nestedItems = item?.isSummary ? item.items : [item]

    nestedItems.forEach((nestedItem) => {
      if (nestedItem?.key && !result.includes(nestedItem.key)) {
        result.push(nestedItem.key)
      }
    })

    return result
  }, [])
}

const RatingChangeNotifications = ({ enabled = true, profileId = null }) => {
  const settings = useSelector((store) => store.settings)
  const [items, setItems] = useState([])
  const [index, setIndex] = useState(0)

  const loadNotifications = useCallback(() => {
    if (!enabled || !profileId) {
      setItems([])
      setIndex(0)
      return
    }

    userRating
      .getChangeNotifications()
      .then((response) => {
        const nextItems = response?.data?.data ?? response?.data ?? []
        setItems(prepareNotificationItems(nextItems))
        setIndex(0)
      })
      .catch((error) => console.error(error))
  }, [enabled, profileId])

  const confirmNotifications = useCallback((notifications) => {
    const keys = getNotificationKeys(notifications)

    if (!keys.length) return

    userRating
      .confirmChangeNotifications({ keys })
      .catch((error) => console.error(error))
  }, [])

  useEffect(() => {
    loadNotifications()
  }, [loadNotifications])

  const current = items[index]
  const isUp = current?.direction === 'up'
  const isMixed = current?.direction === 'mixed'
  const isMotionDisabled = isInterfaceAnimationsDisabled(settings)
  const confetti = useMemo(
    () => Array.from({ length: 18 }, (_, itemIndex) => itemIndex),
    [],
  )

  const close = () => {
    if (index < items.length - 1) {
      setIndex((prev) => prev + 1)
      return
    }

    setItems([])
    setIndex(0)
    confirmNotifications(items)
  }

  return (
    <Modal
      active={!!current}
      setActive={close}
      className={s.modal}
      classNameBackground={s.background}
      disableBackdropDismiss
      fullScreenMobile={false}
    >
      {!!current && (
        <div className={s.card}>
          <IcoRemove className={s.close} onClick={close} />
          {!isMotionDisabled && (
            <div className={s.confetti} aria-hidden="true">
              {confetti.map((item) => (
                <span
                  key={item}
                  style={{
                    '--x': `${(item % 9) - 4}rem`,
                    '--y': `${Math.floor(item / 3) - 3.5}rem`,
                    '--r': `${item * 23}deg`,
                  }}
                />
              ))}
            </div>
          )}
          <div className={isUp ? s.icon : `${s.icon} ${s.iconDown}`}>
            {isUp ? '★' : '!'}
          </div>
          <h3>
            {isUp ? 'Поздравляем!' : isMixed ? 'Рейтинг изменился' : 'Ой-ой!'}
          </h3>
          <p>
            {current.isSummary ? (
              <>{getSummaryText(current.direction)}</>
            ) : isUp ? (
              <>
                Место в рейтинге {current.label} поднялось{' '}
                <b>{getPositionText(current)}</b>. Так держать!
              </>
            ) : (
              <>
                Место в рейтинге {current.label} опустилось{' '}
                <b>{getPositionText(current)}</b>. Поднажми и у тебя всё получится!
              </>
            )}
          </p>
          {current.isSummary && (
            <ul className={s.summaryList}>
              {current.items.map((item, itemIndex) => (
                <li key={`${item.key || item.label}-${item.position}-${itemIndex}`}>
                  <span>{item.label}</span>
                  <b>{getPositionText(item)}</b>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  )
}

export default RatingChangeNotifications
